import { useEffect, useMemo, useRef, useState } from "react";
import { FCX } from "@datassential/platform-ui-lib";
import cn from "classnames";
import Ribbon, { RibbonItem } from "@components/Ribbon/Ribbon";
import LTOItem from "@pages/_shared/LTOItem/LTOItem";
import InputRadio from "@components/new/InputRadio/InputRadio";
import useLocalStorageState from "@hooks/useLocalStorageState";
import { iLTO } from "@models/LTO";
import { LAUNCHES_ITEMS_TYPE_STORAGE_KEY } from "../../Launches.constants";
import "./RecentlyLaunchedCarousel.styles.scss";

type ItemsType = "recent" | "top";

const TILE_WIDTH = 300;

const AUTO_SLIDE_INTERVAL_MS = 10000;

interface Props {
    recentItems: iLTO[];
    topItems: iLTO[];
    topPerformingMonths?: number;
}

const RecentlyLaunchedCarousel: FCX<Props> = ({
    recentItems,
    topItems,
    className,
}) => {
    const [itemsType, setItemsType] = useLocalStorageState<ItemsType>(
        LAUNCHES_ITEMS_TYPE_STORAGE_KEY,
        "recent",
    );

    const items = itemsType === "top" ? topItems : recentItems;

    const ribbonItems: RibbonItem[] = useMemo(
        () => items.map(item => ({ id: item.id, title: item.name, lto: item })),
        [items],
    );

    const containerRef = useRef<HTMLDivElement | null>(null);
    const [maxSlidesPerView, setMaxSlidesPerView] = useState(1);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const observer = new ResizeObserver(() => {
            setMaxSlidesPerView(Math.max(1, Math.floor(el.offsetWidth / TILE_WIDTH)));
        });
        observer.observe(el);

        return () => observer.disconnect();
    }, []);

    return (
        <div ref={containerRef} className={cn("RecentlyLaunchedCarousel", className)}>
            <div className="RecentlyLaunchedCarousel__radios">
                <InputRadio
                    label="Most Recent"
                    isActive={itemsType === "recent"}
                    onChange={() => setItemsType("recent")}
                />
                <InputRadio
                    label={`Top Performing (past 6 months)`}
                    isActive={itemsType === "top"}
                    onChange={() => setItemsType("top")}
                />
            </div>

            <Ribbon
                className="RecentlyLaunchedCarousel__ribbon"
                items={ribbonItems}
                minTileWidth={TILE_WIDTH}
                maxSlidesPerView={maxSlidesPerView}
                autoSlideIntervalMs={AUTO_SLIDE_INTERVAL_MS}
                renderItem={(item) => (
                    <div key={`${itemsType}-${item.id}`} className="RecentlyLaunchedCarousel__item">
                        <LTOItem data={item.lto as iLTO} location="food profile" />
                    </div>
                )}
            />
        </div>
    );
};

export default RecentlyLaunchedCarousel;
